import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "../../style/scss/Resume1.scss";
import UserData from "../Controls/UserData";
import { DateTime } from "luxon";

function useResumeData(id) {
  const [userData] = useState(UserData.UserData());
  const [career, setCareer] = useState({});
  const [skills, setSkills] = useState([]);
  const [employments, setEmployments] = useState([]);
  const [educations, setEducations] = useState([]);
  const [certs, setCerts] = useState([]);

  useEffect(() => {
    const c = (userData.careers || []).find((x) => x.id === id || x.ID === id) || {};
    setCareer(c);
    const careerSkills = c.Skills || c.skills || [];
    setSkills(
      (userData.personSkills || [])
        .filter((ps) => careerSkills.indexOf(ps.SkillID || ps.skillID) >= 0)
        .sort((a, b) =>
          (a.ExperienceMonths || a.experienceMonths || 0) >
          (b.ExperienceMonths || b.experienceMonths || 0)
            ? -1
            : 1
        )
    );
    const careerEmployments = c.Employments || c.employments || [];
    setEmployments(
      (userData.employmentHistory || [])
        .filter((eh) => careerEmployments.indexOf(eh.id || eh.ID) >= 0)
        .sort((a, b) => ((a.StartDate || a.startDate) > (b.StartDate || b.startDate) ? -1 : 1))
    );
    setEducations(
      [...(userData.education || [])].sort((a, b) =>
        (a.YearStart || a.yearStart) > (b.YearStart || b.yearStart) ? -1 : 1
      )
    );
    setCerts(
      [...(userData.certifications || [])].sort((a, b) =>
        (a.DateReceived || a.dateReceived) > (b.DateReceived || b.dateReceived) ? -1 : 1
      )
    );
  }, [userData, id]);

  return { userData, career, skills, employments, educations, certs };
}

function ContactBlock({ userData }) {
  const person = userData.person || {};
  const loc = userData.locations && userData.locations[0];
  return (
    <>
      <div className="Resume_NameTitle">
        {person.FirstName || person.firstName}&nbsp;{person.LastName || person.lastName}
      </div>
      {loc ? (
        <div className="Resume_ContactInfo">
          <div className="Resume_Address">
            {loc.Address1 || loc.address1},{loc.CityName || loc.cityName},
            {loc.StateName || loc.stateName},{loc.PostalCode || loc.postalCode}
          </div>
        </div>
      ) : (
        "n/a"
      )}
    </>
  );
}

function EmploymentBlock({ employments, userData }) {
  if (!employments?.length) return null;
  return (
    <div className="Resume_Employments">
      <div className="SectionHeader">Employment History</div>
      <ul className="Resume_Employment">
        {employments.map((e) => {
          const employer = (userData.employers || []).find(
            (emp) => (emp.id || emp.ID) === (e.EmployerID || e.employerID)
          );
          const client = (userData.employers || []).find(
            (emp) => (emp.id || emp.ID) === (e.ClientID || e.clientID)
          );
          const intermediate = (userData.employers || []).find(
            (emp) => (emp.id || emp.ID) === (e.IntermediateID || e.intermediateID)
          );
          const company =
            (client || {}).Name ||
            (client || {}).name ||
            (intermediate || {}).Name ||
            (intermediate || {}).name ||
            (employer || {}).Name ||
            (employer || {}).name ||
            "Employer";
          return (
            <li key={e.id || e.ID}>
              {company} -<span className="Resume_JobTitle">{e.Title || e.title}</span>
              <span className="Resume_JobDates">
                ({DateTime.fromISO(e.StartDate || e.startDate).toFormat("MM/yyyy")} -{" "}
                {e.EndDate || e.endDate
                  ? DateTime.fromISO(e.EndDate || e.endDate).toFormat("MM/yyyy")
                  : "Now"}
                )
              </span>
              <div className="Resume_JobDescription">{e.ShortDescription || e.shortDescription}</div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function EducationBlock({ educations }) {
  if (!educations?.length) return null;
  return (
    <div className="Resume_Skills">
      <div className="SectionHeader">Education</div>
      <ul>
        {educations.map((e) => (
          <li key={e.id || e.ID}>
            <span className="Resume_Education_Degree">{e.Degree || e.degree}</span> -
            <span className="Resume_Education_Years">
              {e.YearStart || e.yearStart || "N/A"}-{e.YearEnd || e.yearEnd || "now"}
            </span>
            <span className="Resume_Education_Institution">{e.Institution || e.institution}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CertBlock({ certs }) {
  if (!certs?.length) return null;
  return (
    <div className="Resume_Skills">
      <div className="SectionHeader">Certifications</div>
      <ul>
        {certs.map((c) => (
          <li key={c.id || c.ID}>
            {c.Name || c.name} -{" "}
            {DateTime.fromISO(c.DateReceived || c.dateReceived).toFormat("MM/yyyy") || "unknown"}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Classic layout — original Resume1 section order. */
export function ResumeClassic({ id }) {
  const { userData, career, skills, employments, educations, certs } = useResumeData(id);
  return (
    <div className="resume" data-testid="resume-classic" data-resume-format="classic">
      <ContactBlock userData={userData} />
      <div className="Resume_Objective">
        Objective:{" "}
        {career && (career.Objective || career.objective)
          ? career.Objective || career.objective
          : "n/a"}
      </div>
      {skills?.length ? (
        <div className="Resume_Skills">
          <div className="SectionHeader">Skills</div>
          <ul>
            {skills.map((s) => (
              <li key={s.id || s.ID || s.SkillID}>{s.SkillName || s.skillName}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <EmploymentBlock employments={employments} userData={userData} />
      <EducationBlock educations={educations} />
      <CertBlock certs={certs} />
    </div>
  );
}

/** Chronological — employment first. */
export function ResumeChronological({ id }) {
  const { userData, career, skills, employments, educations, certs } = useResumeData(id);
  return (
    <div className="resume resumeChronological" data-testid="resume-chronological" data-resume-format="chronological">
      <ContactBlock userData={userData} />
      <div className="Resume_Objective">
        Objective:{" "}
        {career && (career.Objective || career.objective)
          ? career.Objective || career.objective
          : "n/a"}
      </div>
      <EmploymentBlock employments={employments} userData={userData} />
      {skills?.length ? (
        <div className="Resume_Skills">
          <div className="SectionHeader">Skills</div>
          <ul>
            {skills.map((s) => (
              <li key={s.id || s.ID || s.SkillID}>{s.SkillName || s.skillName}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <EducationBlock educations={educations} />
      <CertBlock certs={certs} />
    </div>
  );
}

/** Skills-first — skills with months featured. */
export function ResumeSkillsFirst({ id }) {
  const { userData, career, skills, employments, educations, certs } = useResumeData(id);
  return (
    <div className="resume resumeSkillsFirst" data-testid="resume-skills-first" data-resume-format="skillsFirst">
      <ContactBlock userData={userData} />
      <div className="Resume_Objective">
        Objective:{" "}
        {career && (career.Objective || career.objective)
          ? career.Objective || career.objective
          : "n/a"}
      </div>
      {skills?.length ? (
        <div className="Resume_Skills">
          <div className="SectionHeader">Core skills</div>
          <ul>
            {skills.map((s) => {
              const months = s.ExperienceMonths ?? s.experienceMonths ?? 0;
              return (
                <li key={s.id || s.ID || s.SkillID}>
                  <strong>{s.SkillName || s.skillName}</strong>
                  {months > 0 ? ` — ${months} months` : ""}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
      <EmploymentBlock employments={employments} userData={userData} />
      <EducationBlock educations={educations} />
      <CertBlock certs={certs} />
    </div>
  );
}

export default function ResumeView() {
  const { id: idStr } = useParams();
  const id = parseInt(idStr, 10);
  const userData = UserData.UserData();
  const career = (userData.careers || []).find((c) => c.id === id || c.ID === id);
  const format = Number(career?.ResumeFormat ?? career?.resumeFormat ?? 1);

  if (format === 2) return <ResumeChronological id={id} />;
  if (format === 3) return <ResumeSkillsFirst id={id} />;
  return <ResumeClassic id={id} />;
}
